import { Link } from 'react-router-dom';
import { ArrowRight, Star, ExternalLink } from 'lucide-react';
import SectionLabel from '../components/SectionLabel';
import HoverVideo from '../components/HoverVideo';
import BookingCTA from '../components/BookingCTA';
import SEO from '../components/SEO';
import { LOCAL_BUSINESS_SCHEMA, WEBSITE_SCHEMA, ORGANIZATION_SCHEMA } from '../lib/schema';
import { PARTNERS } from '../mock/data';
import { useLang } from '../context/LanguageContext';

const Home = () => {
  const { c, ui, lp } = useLang();
  const t = ui.home;
  const featured = c.portfolioProjects.filter(p => p.featured).slice(0, 6);

  return (
    <div className="bg-[#0a0a0b] text-white">
      <SEO
        path="/"
        title={t.seoTitle}
        appendSite={false}
        description={t.seoDesc}
        keywords={t.keywords}
        jsonLd={[LOCAL_BUSINESS_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA]}
      />
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end pb-20 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/9028761/pexels-photo-9028761.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920" alt="Premium car detailing" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 w-full">
          <SectionLabel>{t.heroLabel}</SectionLabel>
          <h1 className="mt-6 text-5xl md:text-7xl lg:text-[88px] font-medium leading-[1.02] tracking-tight max-w-5xl">
            {t.heroTitle[0]}<br />{t.heroTitle[1]}
          </h1>
          <p className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed">
            {t.heroSubtitle}
          </p>
          <div className="mt-10">
            <BookingCTA size="lg" />
          </div>
          <div className="mt-5">
            <Link to={lp('mobile')} data-testid="hero-athome-link" className="inline-flex items-center gap-2 text-white/70 hover:text-[#7aa3ff] text-sm md:text-base transition-colors">
              {t.heroAtHome} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="relative -mt-4 z-10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="rounded-2xl border border-white/10 bg-[#111114]/90 backdrop-blur-md p-8 md:p-10 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]">
            {c.heroStats.map((s, i) => (
              <div key={i} className={`${i > 0 ? 'md:border-l md:border-white/10 md:pl-8' : ''}`}>
                <div className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">{s.value}</div>
                <div className="mt-2 text-white/55 text-xs md:text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO / LOCATIE */}
      <section className="pt-24 lg:pt-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <SectionLabel>{t.introLabel}</SectionLabel>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.08]">{t.introTitle}</h2>
            <p className="mt-5 text-white/65 leading-relaxed">{t.introText}</p>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div data-testid="home-loc-shop" className="rounded-xl border border-white/10 bg-white/[0.02] p-7">
              <h3 className="text-white text-xl md:text-2xl font-medium tracking-tight">{t.locShopTitle}</h3>
              <p className="mt-3 text-white/60 text-sm leading-relaxed">{t.locShopText}</p>
            </div>
            <Link to={lp('mobile')} data-testid="home-loc-home" className="group rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-[#2f6bff]/40 transition-all p-7 flex flex-col">
              <h3 className="text-white text-xl md:text-2xl font-medium tracking-tight">{t.locHomeTitle}</h3>
              <p className="mt-3 text-white/60 text-sm leading-relaxed flex-1">{t.locHomeText}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-[#7aa3ff] text-sm group-hover:text-white transition-colors">
                {t.locHomeCta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="pt-28 lg:pt-36 pb-24 lg:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <SectionLabel>{t.servicesLabel}</SectionLabel>
              <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">{t.servicesTitle}</h2>
              <p className="mt-5 text-white/60 max-w-lg">{t.servicesText}</p>
            </div>
            <Link to={lp('diensten')} className="group inline-flex items-center gap-2 text-white text-sm hover:text-[#2f6bff] transition-colors">
              {t.servicesAll} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {c.services.slice(0, 4).map((s, i) => (
              <Link key={s.slug} to={lp('service', { slug: s.slug })} className={`group relative overflow-hidden rounded-xl ${i === 0 ? 'md:row-span-3 md:min-h-[680px]' : 'min-h-[280px]'}`}>
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                <div className="relative h-full flex flex-col justify-end p-7">
                  <div className="text-white/50 text-xs tracking-[0.3em] mb-3">{s.number}</div>
                  <h3 className="text-white text-2xl md:text-3xl font-medium tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-white/65 text-sm">{s.short}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-white text-sm group-hover:text-[#2f6bff] transition-colors">
                    {t.discoverMore} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
            <div className="max-w-2xl">
              <SectionLabel>{t.portfolioLabel}</SectionLabel>
              <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
                {t.portfolioTitle[0]}<br />{t.portfolioTitle[1]}
              </h2>
              <p className="mt-5 text-white/60 max-w-lg">
                {t.portfolioText}
              </p>
            </div>
            <Link to={lp('portfolio')} className="group inline-flex items-center gap-2 text-white text-sm hover:text-[#2f6bff] transition-colors">
              {t.portfolioAll} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {featured.map(p => (
              <Link key={p.id} to={lp('portfolio')} className="group rounded-xl overflow-hidden border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex flex-col">
                <div className="relative overflow-hidden">
                  {p.video ? (
                    <HoverVideo src={p.video} alt={p.title} aspect="4/3" />
                  ) : (
                    <div className="aspect-[4/3]">
                      <img src={p.after} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                  <span className="absolute bottom-3 left-3 px-3 py-1.5 rounded-md bg-[#2f6bff] text-white text-[10px] tracking-wider uppercase font-medium">
                    {p.badge}
                  </span>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="text-xl font-medium tracking-tight text-white">{p.title}</h3>
                  <p className="mt-2 text-white/55 text-sm italic flex-1">{p.subtitle}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-white/80 text-sm group-hover:text-[#2f6bff] transition-colors">
                    {t.viewProject} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="py-24 lg:py-28 border-t border-white/5 bg-white/[0.015]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <SectionLabel><span className="mx-auto">{t.partnersLabel}</span></SectionLabel>
            <h2 className="mt-5 text-4xl md:text-5xl font-medium tracking-tight leading-[1.1]">
              {t.partnersTitle[0]}<br />{t.partnersTitle[1]}
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed">
              {t.partnersText}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {PARTNERS.map(p => (
              <div
                key={p.name}
                className="relative group rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/20 transition-all overflow-hidden flex items-center justify-center p-6 md:p-8 min-h-[180px]"
              >
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#2f6bff]/50 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="w-full h-[70px] md:h-[90px] flex items-center justify-center">
                  <img
                    src={p.logo}
                    alt={p.name}
                    className="max-w-full max-h-full w-auto h-auto object-contain mix-blend-screen"
                    loading="lazy"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-10">
            <div>
              <SectionLabel>{t.reviewsLabel}</SectionLabel>
              <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
                {t.reviewsTitle}
              </h2>
            </div>

            <a
              href={c.googleReviews.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-5 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-colors self-start"
            >
              <span className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 48 48" width="26" height="26" aria-hidden="true">
                  <path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
                  <path fill="#34A853" d="M6.3 14.7l6.6 4.8C14.7 15.8 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.1 4 9.3 8.3 6.3 14.7z" />
                  <path fill="#FBBC05" d="M24 44c5.2 0 9.8-2 13.3-5.3l-6.1-5.1c-2 1.4-4.5 2.2-7.2 2.2-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.1 39.5 16 44 24 44z" />
                  <path fill="#EA4335" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.1 5.1C41.5 35.7 44 30.3 44 24c0-1.2-.1-2.4-.4-3.5z" />
                </svg>
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white text-2xl font-medium">{c.googleReviews.rating}</span>
                  <div className="flex gap-0.5 text-[#f5b301]">
                    {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
                  </div>
                </div>
                <div className="text-white/55 text-xs mt-0.5 flex items-center gap-1.5">
                  {c.googleReviews.count} {t.googleReviews}
                  <ExternalLink className="w-3 h-3 opacity-70 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </a>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.googleReviews.items.map((r, i) => (
              <div key={i} className="p-6 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex flex-col">
                <div className="flex gap-0.5 mb-4 text-[#f5b301]">
                  {Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
                </div>
                <p className="text-white/85 text-sm leading-relaxed flex-1">&ldquo;{r.text}&rdquo;</p>
                <div className="mt-6 pt-4 border-t border-white/5">
                  <div className="text-white text-sm font-medium">{r.name}</div>
                  <div className="text-white/40 text-xs mt-0.5">{r.vehicle}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to={lp('reviews')} className="group inline-flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors">
              {t.allReviews} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-4xl md:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl mx-auto">{t.ctaTitle}</h2>
          <p className="mt-5 text-white/60">{t.ctaText}</p>
          <div className="mt-10 flex justify-center">
            <BookingCTA size="lg" align="center" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
